/**
 * useSecurityReminder
 *
 * Bookkeeping for the daily reminder modal driven by `securityState`:
 *
 *   soft → modal shows ONCE per local calendar day; dismissible.
 *          `markShown()` writes today's date to `localStorage['ti_security_reminder_last_shown']`.
 *
 *   hard → modal stays mounted unconditionally while `securityState === 'hard'`,
 *          NOT dismissible, NOT marked-shown. The user can only proceed via
 *          `applyUpdate()` (which reloads the page) — at which point a fresh
 *          render will re-evaluate `securityState`. Reopening the tab the
 *          same day re-shows the modal, which is the intended hard-mode
 *          behaviour.
 *
 *          We deliberately do NOT use a session flag for hard mode: any
 *          such flag would be invisible to the user (the modal isn't
 *          dismissible) and would require a controller-side suppression
 *          effect that races with render and flips the modal off mid-paint.
 *
 *   locked / verifying / none → modal is suppressed.
 */

import { useCallback, useState } from 'react';

const SOFT_KEY = 'ti_security_reminder_last_shown';

const todayDateString = () => new Date().toDateString();

const readSoftLastShown = () => {
  try {
    return localStorage.getItem(SOFT_KEY) || null;
  } catch {
    return null;
  }
};

const writeSoftLastShown = () => {
  try {
    localStorage.setItem(SOFT_KEY, todayDateString());
  } catch {
    // Storage failures should not block the UI — re-prompt next render.
  }
};

export const useSecurityReminder = (securityState) => {
  // `tick` is bumped by markShown() to force a re-render after we mutate
  // localStorage. We deliberately do NOT memoize `shouldShowModal` on
  // `securityState` alone because in soft mode the relevant input also
  // includes the localStorage value that markShown() mutates.
  const [, setTick] = useState(0);

  let shouldShowModal = false;
  if (securityState === 'hard') {
    // Unconditionally show while in hard state. The only way out is
    // applyUpdate() (page reload) or natural transition to 'locked'.
    shouldShowModal = true;
  } else if (securityState === 'soft') {
    shouldShowModal = readSoftLastShown() !== todayDateString();
  }
  // 'locked' is handled by the lock screen; 'verifying' / 'none' suppress.

  const dismissible = securityState === 'soft';

  const markShown = useCallback(() => {
    // Only soft mode records a "shown today" timestamp. Hard mode has no
    // recording semantics — see file docstring.
    if (securityState === 'soft') {
      writeSoftLastShown();
      setTick((t) => t + 1);
    }
  }, [securityState]);

  return { shouldShowModal, dismissible, markShown };
};
